import React, { useContext, useState, useEffect } from 'react';
import { Provider, PlanService, SelectedNetwork } from '../../utils/types';
import { Autocomplete, TextField, Link } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { MobileBreakPoint } from '../../theme';
import NoMatch from './NoMatch';
import { DataContext } from '../../containers/InsuranceChecker';
import { EventAction, trackMixpanelEvent } from '../../mixpanelAnalytics';

type Props = {
    service: PlanService | undefined;
    network: SelectedNetwork | undefined;
    handleProviderSelect: (provider:Provider) => void;
    handleStartOver: () => void;
};

const PrimaryCareProvidersContainer = ({ handleProviderSelect, handleStartOver, service, network }:Props): JSX.Element => {
    const [ value, setValue ] = useState('');
    const [ lastTrackedValue, setLastTrackedValue ] = useState('');
    const { providers } = useContext(DataContext);
    const [ focus, setFocus ] = useState(isMobile || window.innerWidth <= MobileBreakPoint);
    const [ open, setOpen ] = useState(isMobile || window.innerWidth <= MobileBreakPoint);
    const [ noMatch, setNoMatch ] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (value && lastTrackedValue !== value) {
                setLastTrackedValue(value);
                trackMixpanelEvent(EventAction.QUERIED_PCP, { value });
            }
        }, 1000);
        return () => clearTimeout(timeout);
    }, [ value ]);

    const handleInputChange = (event:React.SyntheticEvent, value:string):void => {
        setValue(value);
        const selected = providers.filter(p => `${p.firstName} ${p.lastName}` === value);
        if (selected.length) {
            handleProviderSelect(selected[0]);
        }
    };

    const handleKeyDown = (e:React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            setNoMatch(true);
            setOpen(false);
            setFocus(false);
        }
    };

    const handleFocus = () => {
        setOpen(true);
        setFocus(true);
    };

    const handleBlur = () => {
        setOpen(false);
        setFocus(false);
    };

    return (
        <div>
            <Autocomplete
                disablePortal
                id='insurance-plan'
                options={providers}
                sx={{
                    borderRadius: focus ? '40px 40px 0px 0px' : '40px',
                    boxShadow: focus ? '0 0 12px 3px #ccc' : 'none',
                }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                open={open}
                onInputChange={handleInputChange}
                onKeyDown={handleKeyDown}
                autoComplete={true}
                autoHighlight={true}
                popupIcon={<></>}
                renderInput={(params) => <TextField {...params} placeholder='Enter the Primary Care Physician (PCP) on your insurance card'/>}
                componentsProps={ { popper: { placement: 'bottom', sx: { height: '10%' } } }}
                renderOption={(props, option: Provider) => (
                    <li key={option._id} {...props}><img src={option.image} style={ { width: '50px', height: '50px', borderRadius: '100%', margin: '5px' }}/>{option.firstName} {option.lastName}</li>
                )}
                getOptionLabel={(option: Provider):string => { return `${option.firstName} ${option.lastName}`; }}
                noOptionsText={<Link onClick={() => { setNoMatch(true); setOpen(false); setFocus(false); }}>I don't see my provider (PCP)</Link>}
            />
            <NoMatch show={noMatch} service={service} network={network} handleStartOver={handleStartOver}/>
        </div>
    );
};

export default PrimaryCareProvidersContainer;
